import PhotoSwipe           from 'photoswipe';
import { decode as bhDecode } from 'blurhash';
import { Application, Container, Sprite, Texture, Text } from 'pixi.js';
import { renderFilterButtons } from './filter-ui.js';
import { resetCrumbTracking } from './gallery-breadcrumb.js';
import { bindPointerEvents, applyInertia, stepZoom } from './pointer-input.js';
import { showShare, hideShare } from './share.js';
import { createOverlay } from './perf-overlay.js';
import {
    IS_IOS, MOBILE_W, MOUNT_BATCH_IOS_MOVING, TIERS, tierForZoom,
    evictFarTextures, ensureTier, drainQueue, flushDeferredDestroys,
    installContextGuard, removeContextGuard, stopTierLoads,
    registerFade, stepFades, VIEW_ALL_ZOOM, viewAllInsetX, fitCollectionView,
} from './gallery-virt.js';
import { fetchStripManifest, stripEntryFor, stripUrl } from './strips.js';

// ── Layout ────────────────────────────────────────────────────────────────────
const ITEM_W      = 220;
const ITEM_H      = 330;
const GAP         = 0;
const ROW_GAP     = 20;
const SECTION_GAP = ITEM_H * 1.5;

// Spotlight gap: the row nearest the screen centre gets ROW_GAP_FOCUS world-units
// of breathing room (split half above, half below). All other rows hold position.
// Screen gap = ROW_GAP_FOCUS * zoom — subtle at fit zoom, clear when zoomed in.
const ROW_GAP_FOCUS    = 460;  // total world-unit gap around the focused row
const ROW_GAP_SOFTNESS = 200; // world-unit transition width (≈ half a row height)

// Row-label fade: expressed as a multiple of fitZ.
// Labels are fully visible at LABEL_VISIBLE_AT * fitZ and above, fully hidden at LABEL_HIDDEN_AT * fitZ and below.
// Pinned to VIEW_ALL_ZOOM so the labels are already at full opacity on the
// "View all" landing view — that view exists to show them.
const LABEL_VISIBLE_AT = VIEW_ALL_ZOOM;
const LABEL_HIDDEN_AT  = 0.6;  // 0.6 = fully hidden when zoomed out to 60% of fit zoom

// ── Behaviour ─────────────────────────────────────────────────────────────────
const ZOOM_MIN        = 0.05;
const ZOOM_MIN_MOBILE = 0.25; // clamp mobile zoom-out: avoids the draw-call cost of hundreds of unique tier-0 textures
const ZOOM_MAX        = 4;
const ZOOM_LIGHTBOX = 1.8;
const MOUNT_BATCH   = 20;
const CLAMP_MIN_PX_DESKTOP = 220; // min screen-px of gallery kept visible at edges
const CLAMP_MIN_PX_MOBILE  = 60;
const STICKY_ROWS     = true;  // when zoomed in, softly snap Y to nearest row
const STICKY_STRENGTH = 0.25;  // 0–1, how aggressively Y pulls toward row center
const STICKY_ESCAPE   = 0.15;  // velY threshold (px/ms) to break free of sticky pull

// Zoom at which row-snap engages, as a multiple of fitZ:
//   below STICKY_AT * STICKY_SOFT → free
//   between                       → soft pull toward the nearest row
//   at/above STICKY_AT            → hard lock to that row
// This was pinned to labelFullZ (4 × fitZ), which also drives label sizing, the
// zoom ladder and filter jumps — so it couldn't be retuned without moving all
// four. Decoupled and pushed out, because 4 × fitZ grabbed you while you were
// still browsing several looks wide.
// Rough feel on desktop, in screen px per look: 4 ≈ 190, 6 ≈ 284, 8 ≈ 379.
// Raise to snap later, lower to snap sooner.
const STICKY_AT   = 6;
const STICKY_SOFT = 0.8;

// Far-zoom strip gate (mirrors the mobile world view): rows whose baked strip
// underlay has loaded skip per-item sprites below MOUNT_ZOOM (item < ~80
// screen px — where the strip's 60px cells match or beat tier 0). 366 unique
// tier-0 textures/draw calls at fit zoom become ~12 strip sprites. Hysteresis
// stops churn when a pinch settles on the boundary.
const STRIP_MOUNT_ZOOM   = 0.37;
const STRIP_UNMOUNT_ZOOM = 0.34;

// ── GalleryAnimateZoom ────────────────────────────────────────────────────────
class GalleryAnimateZoom {
    constructor() {
        this.viewport = document.getElementById('gallery-viewport');
        if (!this.viewport) return;

        this.data    = [];
        this.collMap = new Map();
        this.items   = [];
        this.groups  = [];
        this.worldW  = 0;
        this.worldH  = 0;

        this.zoom = 1;
        this.panX = 0;
        this.panY = 0;
        this.velX = 0;
        this.velY = 0;
        this.W    = window.innerWidth;
        this.H    = window.innerHeight;

        this.zoomVel  = 0;
        this.zoomCX   = 0;
        this.zoomCY   = 0;
        this.ZOOM_LEVELS = [];
        this.ZOOM_MIN = window.innerWidth < MOBILE_W ? ZOOM_MIN_MOBILE : ZOOM_MIN;
        this.ZOOM_MAX = ZOOM_MAX;

        this._raf         = null;
        this._dragging    = false;
        this._crumbItem   = null;
        this._crumbInitialised = false;
        this._crumbTrackingStarted = false;
        this._crumbVisible = false;
        this._lastPanKey  = '';
        this._activeFilter   = 'all';
        this.lbIndex         = new Map();
        this._mounted        = new Set();
        this._gen            = 0;
        this._tierLoading    = 0;
        this._tierPending    = new Map(); // itemId → { item, tier, dist } — dedup by item

        this._pixiApp        = null;
        this._world          = null;
        this._groupContainers = [];
        this._groupLabels    = []; // PIXI Text per group (counter-scaled to stay screen-size)
        this._visualActive   = null; // row key highlighted in filter bar when Y-locked (not a true filter)
        this._stats          = null;
        this._itemW          = ITEM_W; // gallery-virt eviction reads this

        this.init();
    }

    // ── Init ──────────────────────────────────────────────────────────────────

    destroy() {
        stopTierLoads(this); // abort in-flight thumb fetches so swup nav doesn't keep loading
        if (this._raf !== null) { cancelAnimationFrame(this._raf); this._raf = null; }
        if (this._onResize) window.removeEventListener('resize', this._onResize);
        removeContextGuard(this);
        // PixiApp.destroy() does not reach textures stashed on item objects —
        // release them explicitly so Swup nav doesn't leak VRAM.
        for (const item of this.items) {
            item._texture?.destroy(true);
            item._bhTex?.destroy(true);
            item._texture = null;
            item._bhTex   = null;
        }
        // Strip underlay textures live on groups, not items.
        for (const g of this.groups) {
            g._stripTex?.destroy(true);
            g._stripTex    = null;
            g._stripSprite = null;
        }
        this._pixiApp?.destroy(true);
    }

    async init() {
        this._onResize = () => this.onResize();
        window.addEventListener('resize', this._onResize);
        const manifestP = fetchStripManifest(); // in parallel with the records
        await this.loadData();
        this._stripManifest = await manifestP;  // null → no strip underlays

        this._pixiApp = new Application();
        await this._pixiApp.init({
            backgroundAlpha: 0,
            resizeTo:        this.viewport,
            antialias:       false,
            // Mobile: force 1× — 3× DPR on phones was the dominant fragment-shader
            // and framebuffer cost. Desktop caps at 2× for retina crispness.
            resolution:      window.innerWidth < MOBILE_W
                ? 1
                : Math.min(window.devicePixelRatio || 1, 2),
            autoDensity:     true,
            preference:      'webgl',
        });
        this._pixiApp.ticker.stop(); // we drive our own RAF
        this.viewport.appendChild(this._pixiApp.canvas);
        this._world = new Container();
        this._pixiApp.stage.addChild(this._world);

        installContextGuard(this);

        this.buildLayout();
        this.bindEvents();
        this.checkUrlParam();
        this._wake();

        if (new URL(location.href).searchParams.has('perf')) {
            this._stats = {};
            createOverlay(this);
        }
    }

    // ── Resize ────────────────────────────────────────────────────────────────

    onResize() {
        this.W = window.innerWidth;
        this.H = window.innerHeight;
        this.ZOOM_MIN = this.W < MOBILE_W ? ZOOM_MIN_MOBILE : ZOOM_MIN;
        if (this.worldH > 0) {
            this.zoom = Math.max(this.ZOOM_MIN, this.H / this.worldH);
            this._syncWorld();
        }
    }

    // ── Data ──────────────────────────────────────────────────────────────────

    async loadData() {
        try {
            const [itemsRes, collRes] = await Promise.all([
                fetch(`${window.JK.pbUrl}/api/collections/items/records?` + new URLSearchParams({
                    perPage: 500,
                    filter:  `type='${this.viewport.dataset.filter || 'look'}'`,
                    fields:  'id,collectionId,title,type,keywords,file,collection,img_blurhash,img_w,img_h',
                    expand:  'collection',
                    sort:    'type,title',
                })),
                fetch(`${window.JK.pbUrl}/api/collections/collections/records?` + new URLSearchParams({
                    perPage: 100,
                    sort:    'order',
                })),
            ]);
            const itemsJson = await itemsRes.json();
            const collJson  = await collRes.json();
            this.data    = itemsJson.items || [];
            const natural = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
            this.data.sort((a, b) => natural.compare(a.title || '', b.title || ''));
            this.collMap = new Map((collJson.items || []).map(c => [c.id, c]));
            for (const item of this.data) {
                const ec = item.expand?.collection;
                if (ec && !this.collMap.has(ec.id)) this.collMap.set(ec.id, ec);
            }
        } catch (err) {
            console.error('GalleryAnimateZoom: load failed', err);
        }
    }

    // ── Layout ────────────────────────────────────────────────────────────────

    buildLayout() {
        // Clear any existing group containers from the previous layout
        for (const c of this._groupContainers) {
            if (this._world) this._world.removeChild(c);
        }
        this._groupContainers = [];
        this._groupLabels    = [];

        // Unmount all sprites before rebuilding
        for (const item of this._mounted) {
            if (item._sprite) item._sprite.destroy({ texture: false });
            item._el = null; item._sprite = null;
        }
        this._mounted.clear();

        const nameToId = new Map([...this.collMap.entries()].map(([id, c]) => [c.name || c.title, id]));

        const keyFor = item => {
            let key = item.collection || item.expand?.collection?.id;
            if (!key) {
                const m = (item.title || '').match(/^([A-Z]{2}\d{2})\b/);
                key = (m && nameToId.get(m[1])) || '__none__';
            }
            return key;
        };

        const filter = this.viewport.dataset.filter || 'look';
        const sections = [
            { items: this.data.filter(i => i.type === filter) },
        ];

        this.items  = [];
        this.groups = [];
        this.worldW = 0;
        let y = 0;

        for (const section of sections) {
            if (!section.items.length) continue;

            const byId = new Map();
            for (const item of section.items) {
                const key = keyFor(item);
                if (!byId.has(key)) byId.set(key, []);
                byId.get(key).push(item);
            }

            const orderedKeys = [...this.collMap.keys()].filter(k => byId.has(k));
            if (byId.has('__none__')) orderedKeys.push('__none__');

            const isAccessory = filter === 'accessory';
            for (let gi = 0; gi < orderedKeys.length; gi++) {
                const key  = orderedKeys[gi];
                const coll = this.collMap.get(key);
                const row  = byId.get(key) || [];
                let x = 0, rowH = 0;
                for (const item of row) {
                    if (isAccessory) {
                        // Fixed frame (ITEM_W × ITEM_H); contain the image inside it, centred.
                        item._h = ITEM_H;
                        const aspect = (item.img_w && item.img_h) ? item.img_w / item.img_h : ITEM_W / ITEM_H;
                        if (aspect > ITEM_W / ITEM_H) {
                            item._dw = ITEM_W;
                            item._dh = ITEM_W / aspect;
                        } else {
                            item._dh = ITEM_H;
                            item._dw = ITEM_H * aspect;
                        }
                        item._dx = (ITEM_W - item._dw) / 2;
                        item._dy = (ITEM_H - item._dh) / 2;
                    } else {
                        item._h  = (item.img_w && item.img_h)
                            ? Math.round(ITEM_W * item.img_h / item.img_w)
                            : ITEM_H;
                        item._dw = ITEM_W;
                        item._dh = item._h;
                        item._dx = 0;
                        item._dy = 0;
                    }
                    item._x          = x;
                    item._y          = y;
                    item._groupIdx   = gi;
                    item._el         = null;
                    item._loadedTier = -1;
                    item._collKey    = key;
                    this.items.push(item);
                    x    += ITEM_W + GAP;
                    rowH  = Math.max(rowH, item._h);
                }

                // Create a PixiJS Container for this group row
                const groupContainer = new Container();
                groupContainer.x = 0;
                // y will be set properly in _syncWorld; initialise to rowY
                groupContainer.y = y;
                this._world.addChild(groupContainer);
                this._groupContainers.push(groupContainer);

                const labelText = coll?.name || coll?.title || '';
                let label = null;
                if (labelText) {
                    label = new Text({
                        text: labelText,
                        style: {
                            fontFamily: 'Plantin Std, serif',
                            fontSize: 40,
                            fill: this.viewport.dataset.theme === 'light' ? 0x000000 : 0xffffff,
                        },
                    });
                    label.anchor.set(1, 0);        // right-align, top-pinned
                    label.y = 0;
                    groupContainer.addChild(label);
                }
                this._groupLabels.push(label);

                const groupObj = { key, label: labelText, rowY: y, rowH, width: x, items: row };
                this.groups.push(groupObj);
                this._loadStrip(groupContainer, groupObj);
                this.worldW = Math.max(this.worldW, x);
                y += rowH + ROW_GAP;
            }

            y += SECTION_GAP - ROW_GAP;
        }

        this.worldH = y > 0 ? y - SECTION_GAP : 0;
        const fitZ  = this.worldH > 0 ? this.H / this.worldH : 1;

        if (filter === 'accessory') {
            this.zoom = Math.max(this.ZOOM_MIN, fitZ * 0.75);
            this.panX = 80;
            this.panY = 80;
        } else {
            this.zoom = Math.max(this.ZOOM_MIN, fitZ * VIEW_ALL_ZOOM);
            this.panX = viewAllInsetX(this.W);
            this.panY = (this.H - this.worldH * this.zoom) / 2;
        }
        this.fitZ       = fitZ;
        this.labelFullZ = Math.min(fitZ * 4, ZOOM_MAX); // Y-lock + label full-size threshold
        this.ZOOM_LEVELS = [
            Math.max(fitZ * 0.25, this.ZOOM_MIN),
            Math.max(fitZ * 0.5,  this.ZOOM_MIN),
            fitZ,
            this.labelFullZ,
            Math.min(fitZ * 10, ZOOM_MAX),
            ZOOM_MAX,
        ]
            .sort((a, b) => a - b)
            .filter((z, i, a) => i === 0 || z > a[i - 1] * 1.2);

        this.items.forEach((item, idx) => this.lbIndex.set(item.id, idx));

        this._renderFilterButtons();
        this._syncWorld();
    }

    // ── Strip underlay ────────────────────────────────────────────────────────
    // One baked-strip sprite per row, under the item sprites (carried over
    // from the mobile world view). Two wins: the zoomed-out wall paints from
    // ~12 small requests instead of ~366 thumb fetches, and below the strip
    // gate rows need no per-item sprites at all (12 draw calls, not 366).

    _loadStrip(container, g) {
        const type  = this.viewport.dataset.filter || 'look';
        const entry = stripEntryFor(this._stripManifest, type, g);
        if (!entry) return; // missing/stale → row keeps blurhash placeholders

        // Instant stand-in while the baked strip downloads: an N×1 blurhash-tint
        // texture stretched across the row (the mobile world view's mosaic,
        // as a Pixi sprite). Marking the row _stripReady from frame one keeps
        // the far-zoom gate shut — without it the first ~500ms mounted ~40
        // per-item sprites that were then shed when the strip arrived, which
        // read as the page loading twice.
        const canvas  = document.createElement('canvas');
        canvas.width  = g.items.length;
        canvas.height = 1;
        const ctx = canvas.getContext('2d');
        g.items.forEach((item, i) => {
            let rgb = 'rgb(68,68,68)';
            try {
                if (item.img_blurhash) {
                    const px = bhDecode(item.img_blurhash, 1, 1);
                    rgb = `rgb(${px[0]},${px[1]},${px[2]})`;
                }
            } catch {}
            ctx.fillStyle = rgb;
            ctx.fillRect(i, 0, 1, 1);
        });
        const tintTex    = Texture.from(canvas);
        tintTex.source.scaleMode = 'nearest'; // crisp per-item cells, not a smear
        const tintSprite = new Sprite(tintTex);
        tintSprite.width   = g.width;
        tintSprite.height  = g.rowH;
        tintSprite.visible = this._activeFilter === 'all' || g.key === this._activeFilter;
        container.addChildAt(tintSprite, 0);
        g._stripSprite = tintSprite;
        g._stripTex    = tintTex;
        g._stripReady  = true;

        if (!this._loadAbort) this._loadAbort = new AbortController();
        fetch(stripUrl(entry), { signal: this._loadAbort.signal })
            .then(r => { if (!r.ok) throw new Error('HTTP ' + r.status); return r.blob(); })
            .then(blob => createImageBitmap(blob))
            .then(bitmap => {
                if (this._destroyed || container.destroyed || !g._stripSprite) return;
                // Swap the texture on the standing tint sprite — atomic, like
                // tier swaps (no overlap fade; see the accessories-flicker fix).
                // Keeping the sprite means setFilter/destroy refs stay valid.
                const tex = Texture.from(bitmap);
                const old = g._stripTex;
                g._stripSprite.texture = tex;
                g._stripSprite.width   = g.width;
                g._stripSprite.height  = g.rowH;
                g._stripTex = tex;
                old?.destroy(true);
                this._wake();
            })
            .catch(() => {}); // aborted nav / network — the tint mosaic remains
    }

    // Context restore: strip textures died with the WebGL context — rebuild.
    _reloadStrips() {
        for (let i = 0; i < this.groups.length; i++) {
            const g = this.groups[i];
            if (g._stripSprite) { g._stripSprite.destroy({ texture: false }); g._stripSprite = null; }
            g._stripTex   = null;
            g._stripReady = false;
            this._loadStrip(this._groupContainers[i], g);
        }
    }

    // ── Focus gap helpers ─────────────────────────────────────────────────────
    // The row nearest the screen centre gets ROW_GAP_FOCUS world-units of extra
    // space around it (±ROW_GAP_FOCUS/2). All other rows converge smoothly to
    // their base position via a sigmoid centred on the screen's world-y.

    _groupOffset(gi) {
        // Gap fades in from 0 at fit zoom to full at the next zoom level,
        // so it grows naturally as you zoom in rather than appearing suddenly.
        const fitZ      = this.fitZ ?? this.zoom;
        const targetZ   = this.labelFullZ ?? fitZ * 4;
        const zoomScale = Math.max(0, Math.min(1, (this.zoom - fitZ) / (targetZ - fitZ)));
        if (zoomScale === 0) return 0;

        const focusCenterY = (this.H / 2 - this.panY) / this.zoom;
        const g = this.groups[gi];
        const groupCenterY = g.rowY + g.rowH / 2;
        const x = (groupCenterY - focusCenterY) / ROW_GAP_SOFTNESS;
        const s = 1 / (1 + Math.exp(-x));
        return ROW_GAP_FOCUS * zoomScale * (s - 0.5);
    }

    _itemVisualY(item) {
        return item._y + this._groupOffset(item._groupIdx);
    }

    // ── Sprite virtualiser ────────────────────────────────────────────────────

    updateScene() {
        const { groups, zoom } = this;
        const tier = tierForZoom(zoom, ITEM_W);

        const vl = -this.panX / zoom;
        const vt = -this.panY / zoom;
        const vr = vl + this.W / zoom;
        const vb = vt + this.H / zoom;

        const cx = vl + (vr - vl) / 2;
        const cy = vt + (vb - vt) / 2;
        // Mobile: tight buffer so most cached items are unmounted and therefore
        // safe to evict. Desktop keeps the generous one-viewport buffer.
        const buf = (this.W < MOBILE_W) ? this.H / zoom * 0.25 : this.H / zoom;

        const moving   = this._dragging || Math.abs(this.velX) > 0.3 || Math.abs(this.velY) > 0.3 || Math.abs(this.zoomVel) > 0.001;
        const maxMount = moving
            ? (IS_IOS ? MOUNT_BATCH_IOS_MOVING : 5)
            : (this.W < MOBILE_W ? 8 : MOUNT_BATCH);

        // Strip gate (hysteresis): below it, strip-backed rows render as their
        // single underlay sprite — their items are skipped (not visited) and
        // the sweep below unmounts any leftovers.
        const stripGate = this._stripGateOpen ? STRIP_UNMOUNT_ZOOM : STRIP_MOUNT_ZOOM;
        this._stripGateOpen = zoom >= stripGate;
        // Debounce tier upgrades: wait 150ms after motion stops before queuing
        // loads. Prevents rapid pinch from thrashing tier 0→1→2 and wasting bandwidth.
        const now = performance.now();
        if (moving) this._lastMoveAt = now;
        const settled = !moving && (now - (this._lastMoveAt || 0)) > 150;

        const gen = ++this._gen;

        let mounted = 0;
        let pending = false;

        // gvy(gi): visual top of group gi in _world local space
        const gvy = gi => groups[gi].rowY + this._groupOffset(gi);

        // Binary search: first group whose visual bottom is inside the buffered viewport
        const gs = groups;
        let lo = 0, hi = gs.length - 1, firstGi = gs.length;
        while (lo <= hi) {
            const mid = (lo + hi) >> 1;
            if (gvy(mid) + gs[mid].rowH > vt - buf) { firstGi = mid; hi = mid - 1; }
            else lo = mid + 1;
        }

        const isMobile = this.W < MOBILE_W;
        for (let gi = firstGi; gi < gs.length; gi++) {
            const g = gs[gi];
            if (gvy(gi) > vb + buf) break;
            if (!this._stripGateOpen && g._stripReady) continue; // strip carries this row
            const vy = gvy(gi);
            for (const item of g.items) {
                if (item._x >= vr + buf || item._x + ITEM_W <= vl - buf) {
                    if (item._el) this._unmountItem(item);
                    continue;
                }
                item._visitGen = gen;
                if (!item._el) {
                    if (mounted >= maxMount) { pending = true; continue; }
                    this._mountItem(item);
                    mounted++;
                }
                if (settled) {
                    const dist = Math.abs(item._x + ITEM_W / 2 - cx) + Math.abs(vy + item._h / 2 - cy);
                    // Mobile: only items in the unbuffered viewport may load tier 2
                    // (~16× the texture memory of tier 1). Buffered-but-offscreen
                    // items max out at tier 1 — they upgrade once they come fully on.
                    const onscreen = item._x < vr && item._x + ITEM_W > vl
                                  && vy < vb && vy + g.rowH > vt;
                    const cappedTier = (isMobile && !onscreen) ? Math.min(tier, 1) : tier;
                    ensureTier(this, item, cappedTier, dist);
                }
            }
        }

        for (const item of this._mounted) {
            if (item._visitGen !== gen) this._unmountItem(item);
        }

        if (settled) {
            drainQueue(this);
            evictFarTextures(this);
        } else if (isMobile) {
            // Run eviction every frame on mobile during motion — needed because
            // the byte budget alone never trips on tier-0-dominant content, and
            // the count cap (mobile only) is the actual draw-call bound.
            // Cheap when nothing to do (early-return inside).
            evictFarTextures(this);
        }
        // Keep RAF alive during the post-motion debounce window so tier logic runs once it elapses.
        return pending || (!moving && !settled);
    }

    // gallery-virt's evictFarTextures uses this to compute world-Y for distance.
    _itemY(item) { return this._itemVisualY(item); }

    // ── Filter ────────────────────────────────────────────────────────────────

    _itemVisible(item) {
        return this._activeFilter === 'all' || item._collKey === this._activeFilter;
    }

    setFilter(key, { navigateToFirst = true } = {}) {
        if (!key) return;
        const showAll = key === 'all';
        this._activeFilter = key;
        resetCrumbTracking(this);
        for (const item of this._mounted) {
            const v = this._itemVisible(item);
            if (item._sprite)   item._sprite.visible   = v;
            if (item._bgSprite) item._bgSprite.visible = v;
        }
        for (let i = 0; i < this.groups.length; i++) {
            const lbl = this._groupLabels[i];
            if (lbl) lbl.visible = showAll || this.groups[i].key === key;
            // Strip underlays follow the filter — otherwise hidden rows still paint.
            const strip = this.groups[i]._stripSprite;
            if (strip) strip.visible = showAll || this.groups[i].key === key;
        }
        if (this._pixiApp) this._pixiApp.renderer.render(this._pixiApp.stage);
        this._renderFilterButtons();
        if (navigateToFirst) {
            if (showAll) this._fitVisible();
            else this.frameCollection(key);
        }

        // Mirror the filter into the URL, as the world view does, so a filtered
        // view survives a reload and can be shared. Other params are preserved.
        const params = new URLSearchParams(location.search);
        const label = this.groups.find(g => g.key === key)?.label;
        if (showAll) {
            params.delete('filter');
        } else if (label) {
            params.set('filter', label.toLowerCase());
        }
        const qs = params.toString();
        history.replaceState(null, '', location.pathname + (qs ? `?${qs}` : ''));
    }

    /**
     * Snap the view to frame one collection end to end, with its label in the
     * left margin. Replaces the old "zoom to the first look" jump, which landed
     * at a fixed 4 × fitZ regardless of how long the collection actually was.
     */
    frameCollection(key) {
        const group = this.groups.find(g => g.key === key);
        if (!group || !group.width) return;
        Object.assign(this, fitCollectionView(this, group));
        this.velX = this.velY = this.zoomVel = 0;
        this.clamp();
        this._wake();
    }

    _fitVisible() {
        const visible = this.items.filter(i => this._itemVisible(i));
        if (!visible.length) return;

        // Compute raw (gap-less) bounds to determine target zoom
        let minX = Infinity, minRawY = Infinity, maxX = -Infinity, maxRawY = -Infinity;
        for (const item of visible) {
            minX    = Math.min(minX,    item._x);
            minRawY = Math.min(minRawY, item._y);
            maxX    = Math.max(maxX,    item._x + ITEM_W);
            maxRawY = Math.max(maxRawY, item._y + item._h);
        }
        const pad     = 60;
        const targetZ = Math.min(
            this.W / (maxX - minX + pad * 2),
            this.H / (maxRawY - minRawY + pad * 2),
            ZOOM_MAX
        );

        // Compute visual (gap-adjusted) bounds at targetZ for centering
        // Use raw y for centering — focus offset (±30wu) is small relative to layout
        let minVY = Infinity, maxVY = -Infinity;
        for (const item of visible) {
            minVY = Math.min(minVY, item._y);
            maxVY = Math.max(maxVY, item._y + item._h);
        }
        const endX = this.W / 2 - ((minX + maxX) / 2) * targetZ;
        const endY = this.H / 2 - ((minVY + maxVY) / 2) * targetZ;

        const startZ = this.zoom, startX = this.panX, startY = this.panY;
        this.zoomVel = 0; this.velX = this.velY = 0;
        const startTime = performance.now();
        const tick = now => {
            const t = Math.min(1, (now - startTime) / 450);
            const e = t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
            this.zoom = startZ + (targetZ - startZ) * e;
            this.panX = startX + (endX - startX) * e;
            this.panY = startY + (endY - startY) * e;
            this._syncWorld();
            this._wake();
            if (t < 1) requestAnimationFrame(tick);
            else this._wake();
        };
        requestAnimationFrame(tick);
    }

    _renderFilterButtons() {
        if (this.groups.length < 2) return;
        const nav = document.getElementById('gallery-filter');
        const active = this._activeFilter !== 'all' ? this._activeFilter : (this._visualActive || 'all');
        renderFilterButtons(nav, this.groups, active, key => {
            this.setFilter(key);
        });
    }

    _mountItem(item) {
        if (!this._pixiApp) return;
        // Initial texture: previously loaded texture > cached blurhash > empty
        let tex = Texture.EMPTY;
        let tint = 0xFFFFFF;
        if (item._texture) tex = item._texture;
        // Strip-backed row + no cached texture: mount invisible (alpha 0) with
        // no placeholder at all — the strip underlay already shows this cell's
        // real pixels. startTierLoad fades the first texture in over it.
        const stripBacked = tex === Texture.EMPTY && this.groups[item._groupIdx]?._stripReady;
        if (tex === Texture.EMPTY && !stripBacked) {
            if (this.W < MOBILE_W) {
                // Mobile: skip the per-item canvas blurhash texture entirely.
                // Use Texture.WHITE (singleton, zero per-item GPU memory) tinted
                // with the blurhash's average color (1×1 decode, 4 bytes).
                if (item._bhTint === undefined) {
                    if (item.img_blurhash) {
                        const px = bhDecode(item.img_blurhash, 1, 1);
                        item._bhTint = (px[0] << 16) | (px[1] << 8) | px[2];
                    } else {
                        item._bhTint = 0x808080;
                    }
                }
                tex  = Texture.WHITE;
                tint = item._bhTint;
            } else if (item.bh_png) {
                if (!item._bhTex) item._bhTex = Texture.from(item.bh_png);
                tex = item._bhTex;
            } else if (item.img_blurhash) {
                if (!item._bhTex) {
                    const BH_W = 8, BH_H = 12;
                    const pixels = bhDecode(item.img_blurhash, BH_W, BH_H);
                    const canvas = document.createElement('canvas');
                    canvas.width = BH_W; canvas.height = BH_H;
                    const ctx = canvas.getContext('2d');
                    const imageData = ctx.createImageData(BH_W, BH_H);
                    imageData.data.set(pixels);
                    ctx.putImageData(imageData, 0, 0);
                    item._bhTex = Texture.from(canvas);
                }
                tex = item._bhTex;
            }
        }

        const sprite  = new Sprite(tex);
        sprite.tint   = tint;
        sprite.x      = item._x + item._dx;
        sprite.y      = item._dy; // group container handles row y offset
        sprite.width  = item._dw;
        sprite.height = item._dh;
        if (stripBacked) sprite.alpha = 0; // fades in on first real texture

        sprite.visible = this._itemVisible(item);

        // Accessory items: white frame behind the contained image.
        const groupContainer = this._groupContainers[item._groupIdx];
        if (item._dw !== ITEM_W || item._dh !== item._h) {
            const bg = new Sprite(Texture.WHITE);
            bg.tint = 0xFFFFFF;
            bg.x = item._x;
            bg.y = 0;
            bg.width = ITEM_W;
            bg.height = item._h;
            bg.visible = sprite.visible;
            groupContainer.addChild(bg);
            item._bgSprite = bg;
        }
        groupContainer.addChild(sprite);
        item._el          = sprite;
        item._sprite      = sprite;
        item._loadedTier  = item._cachedTier ?? -1;
        item._pendingTier = -1;
        this._mounted.add(item);
    }

    _unmountItem(item) {
        if (item._bgSprite) { item._bgSprite.destroy({ texture: false }); item._bgSprite = null; }
        if (item._sprite) item._sprite.destroy({ texture: false }); // removes from parent
        if (item._bhTex) { item._bhTex.destroy(true); item._bhTex = null; }
        item._el          = null;
        item._sprite      = null;
        item._loadedTier  = -1;
        item._pendingTier = -1;
        this._mounted.delete(item);
    }

    // ── URLs ──────────────────────────────────────────────────────────────────

    thumbUrl(item, tierIdx) {
        if (!item.file || !item.collectionId || !item.id) return null;
        return `${window.JK.pbUrl}/api/files/${item.collectionId}/${item.id}/${item.file}?thumb=${TIERS[tierIdx]}`;
    }

    fullUrl(item) {
        if (!item.file || !item.collectionId || !item.id) return '';
        return `${window.JK.pbUrl}/api/files/${item.collectionId}/${item.id}/${item.file}`;
    }

    bestThumbUrl(item) {
        for (let t = TIERS.length - 1; t >= 0; t--) {
            if (item._loadedTier >= t) return this.thumbUrl(item, t);
        }
        return this.thumbUrl(item, 1);
    }

    // ── RAF loop ──────────────────────────────────────────────────────────────

    _tick(now = performance.now()) {
        if (!this._pixiApp) return;
        const dt = Math.min(now - (this._lastTick ?? now), 64);
        this._lastTick = now;
        const t0 = this._stats ? now : 0;
        applyInertia(this, dt);

        const t1 = this._stats ? performance.now() : 0;
        const needsMore = this.updateScene();
        const fading    = stepFades(this, now);

        const t2 = this._stats ? performance.now() : 0;
        this._pixiApp.renderer.render(this._pixiApp.stage);
        // Post-render: textures queued for destruction are now safely out of
        // Pixi's batch state. See deferDestroy in gallery-virt.
        flushDeferredDestroys(this);

        if (this._stats) {
            const n = performance.now();
            this._stats.sceneMs   = (t2 - t1).toFixed(1);
            this._stats.renderMs  = (n - t2).toFixed(1);
            this._stats.frameMs   = (n - t0).toFixed(1);
            this._stats.sprites   = this._mounted.size;
            this._stats.loading   = this._tierLoading;
            this._stats.queue     = this._tierPending.size;
            this._stats.zoom      = this.zoom.toFixed(2);
            this._stats.tier      = tierForZoom(this.zoom, ITEM_W);
            // Count items holding a cached texture (independent of mount state).
            let cached = 0;
            for (const item of this.items) if (item._cachedSrc) cached++;
            this._stats.cached    = cached;
            // Pixi's actual managed-texture array (Pixi v8 GlTextureSystem).
            // `managedTextures` is `Object.values(items)` — Pixi's GCManagedHash
            // nulls slots on remove() instead of deleting them, so we filter to
            // count live entries only. Diverges from `cached` if Pixi internals
            // retain refs we don't track.
            const tm = this._pixiApp?.renderer?.texture?.managedTextures;
            let liveTexs = 0;
            if (tm) for (const t of tm) if (t) liveTexs++;
            this._stats.glTexs    = tm ? liveTexs : '--';
            // Chrome/Edge expose performance.memory; Safari/Firefox don't.
            const m = performance.memory;
            if (m) this._stats.heapMB = Math.round(m.usedJSHeapSize / 1048576);
            if (!this._fpsTimes) this._fpsTimes = [];
            this._fpsTimes.push(n);
            if (this._fpsTimes.length > 60) this._fpsTimes.shift();
            const span = this._fpsTimes.at(-1) - this._fpsTimes[0];
            this._stats.fps = span > 0 ? Math.round((this._fpsTimes.length - 1) / span * 1000) : '--';
        }

        const panKey = `${this.panX.toFixed(1)},${this.panY.toFixed(1)},${this.zoom.toFixed(3)}`;
        if (panKey !== this._lastPanKey || (this._crumbTrackingStarted && !this._crumbVisible)) {
            this._lastPanKey = panKey;
            const center = this._centerItem();
            if (!this._crumbInitialised) {
                this._crumbItem = center;
                this._crumbInitialised = true;
            }
            if (this._crumbTrackingStarted && (!this._crumbVisible || center !== this._crumbItem)) {
                this._crumbItem = center;
                this.updateCrumb(center);
                this._crumbVisible = true;
            }
            if (this._visualActive !== this._lastVisualActive) {
                this._lastVisualActive = this._visualActive;
                this._renderFilterButtons();
            }
        }
        const idle = !this._dragging
            && Math.abs(this.velX)    < 0.01
            && Math.abs(this.velY)    < 0.01
            && Math.abs(this.zoomVel) < 0.0005
            && this._tierLoading === 0
            && !needsMore
            && !fading;
        this._raf = idle ? null : requestAnimationFrame(t => this._tick(t));
    }

    _wake() {
        if (this._raf === null) {
            this._raf = requestAnimationFrame(t => this._tick(t));
        }
    }

    _maybeIdle() {
        if (Math.abs(this.velX) < 0.001 && Math.abs(this.velY) < 0.001 &&
            Math.abs(this.zoomVel) < 0.0005 && !this._dragging) {
            this._raf = null;
        }
    }

    // ── Events ────────────────────────────────────────────────────────────────

    bindEvents() {
        bindPointerEvents(this, this.viewport, { hasCursor: true, trackPinchStartZ: true });
        document.getElementById('zoom-in') ?.addEventListener('click', () => this.zoomIn());
        document.getElementById('zoom-out')?.addEventListener('click', () => this.zoomOut());
    }

    // ── Zoom / pan ────────────────────────────────────────────────────────────

    zoomAt(cx, cy, factor) {
        const newZoom = Math.max(this.ZOOM_MIN, Math.min(ZOOM_MAX, this.zoom * factor));
        const ratio   = newZoom / this.zoom;
        this.panX = cx - ratio * (cx - this.panX);
        this.panY = cy - ratio * (cy - this.panY);
        this.zoom = newZoom;
        this._syncWorld();
    }

    _syncWorld() {
        if (!this._world || !this._pixiApp) return;
        this.clamp();
        this._world.x = this.panX;
        this._world.y = this.panY;
        this._world.scale.set(this.zoom);
        // Spotlight gap: shift each group container by its sigmoid-based offset.
        // Only the row nearest the screen centre gets breathing room; others hold position.
        const { groups } = this;
        const invZ = 1 / this.zoom;
        // Label size grows in line with the spotlight-gap animation.
        // Font is baked at 40px so we only ever downscale (0.4 → 1.0) — stays crisp.
        const fitZ    = this.fitZ ?? this.zoom;
        const targetZ = this.labelFullZ ?? fitZ * 4;
        const zs      = Math.max(0, Math.min(1, (this.zoom - fitZ) / (targetZ - fitZ)));
        // Mobile: hold the smaller label size throughout — scaling up feels cramped.
        const sizeMul = this.W < MOBILE_W ? 0.4 : 0.4 + zs * 0.6; // 16/40 at fit → 40/40 at stickyZ+
        // Fade out below fit zoom; fully visible at LABEL_VISIBLE_AT * fitZ and above.
        const zRatio     = this.zoom / fitZ;
        const labelAlpha = Math.max(0, Math.min(1, (zRatio - LABEL_HIDDEN_AT) / (LABEL_VISIBLE_AT - LABEL_HIDDEN_AT)));
        for (let i = 0; i < groups.length; i++) {
            this._groupContainers[i].y = groups[i].rowY + this._groupOffset(i);
            const lbl = this._groupLabels[i];
            if (lbl) {
                lbl.scale.set(invZ * sizeMul);
                lbl.x = -20 * invZ;           // fixed 20px screen gap to first image
                lbl.alpha = labelAlpha;
                lbl.renderable = labelAlpha > 0;
            }
        }
        // No renderer.render() here — _tick renders once per RAF frame.
    }

    clamp() {
        const clampMinPx = this.W < MOBILE_W ? CLAMP_MIN_PX_MOBILE : CLAMP_MIN_PX_DESKTOP;
        const activeGroup = this._activeFilter !== 'all'
            ? this.groups.find(g => g.key === this._activeFilter)
            : null;

        // Horizontal bound uses the filtered group's width when filtered,
        // so short galleries don't scroll off-screen past clampMinPx.
        const effW = activeGroup ? activeGroup.width : this.worldW;
        const ww = effW * this.zoom;
        this.panX = Math.max(clampMinPx - ww, Math.min(this.W - clampMinPx, this.panX));

        if (activeGroup) {
            this.panY = this.H / 2 - (activeGroup.rowY + activeGroup.rowH / 2) * this.zoom;
            this.velY = 0;
            return;
        }

        const effectiveH = this.worldH + ROW_GAP_FOCUS;
        const wh = effectiveH * this.zoom;
        this.panY = Math.max(clampMinPx - wh, Math.min(this.H - clampMinPx, this.panY));

        // Row-snap/lock when zoomed in — see STICKY_AT for the thresholds.
        this._visualActive = null;
        if (!STICKY_ROWS || this.groups.length < 2) return;
        const stickyZ = Math.min(this.fitZ * STICKY_AT, this.ZOOM_MAX);
        if (!stickyZ || this.zoom <= stickyZ * STICKY_SOFT) return;

        const viewCY = (this.H / 2 - this.panY) / this.zoom;
        let nearest = this.groups[0], bestDist = Infinity;
        for (const g of this.groups) {
            const d = Math.abs(g.rowY + g.rowH / 2 - viewCY);
            if (d < bestDist) { bestDist = d; nearest = g; }
        }
        const targetY = this.H / 2 - (nearest.rowY + nearest.rowH / 2) * this.zoom;

        if (this.zoom >= stickyZ) {
            this.panY = targetY;
            this.velY = 0;
            this._visualActive = nearest.key;
            // Re-clamp X to the locked row's width so it can't scroll off-screen.
            const lockedW = nearest.width * this.zoom;
            this.panX = Math.max(clampMinPx - lockedW, Math.min(this.W - clampMinPx, this.panX));
        } else if (!this._dragging && Math.abs(this.velY) < STICKY_ESCAPE) {
            this.panY += (targetY - this.panY) * STICKY_STRENGTH;
            this.velY *= (1 - STICKY_STRENGTH);
        }
    }

    zoomIn()  { stepZoom(this,  1); }
    zoomOut() { stepZoom(this, -1); }

    animateZoomCenter(targetZ, onComplete) {
        this.animateZoom(targetZ, this.W / 2, this.H / 2, onComplete);
    }

    animateZoom(targetZ, screenCX, screenCY, onComplete) {
        this.zoomVel = 0;
        this._wake();
        const startZ    = this.zoom;
        const startTime = performance.now();
        const tick = now => {
            const t     = Math.min(1, (now - startTime) / 300);
            const eased = t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
            this.zoomAt(screenCX, screenCY, (startZ + (targetZ - startZ) * eased) / this.zoom);
            this._wake();
            if (t < 1) requestAnimationFrame(tick);
            else { this._maybeIdle(); onComplete?.(); }
        };
        requestAnimationFrame(tick);
    }

    // ── Cursor / click ────────────────────────────────────────────────────────

    setCursor(sx, sy) {
        this.viewport.style.cursor = this.itemAt(sx, sy) ? 'zoom-in' : 'grab';
    }

    handleClick(sx, sy) {
        const item = this.itemAt(sx, sy);
        if (!item) return;
        const lbThreshold = this.W < 768 ? ZOOM_LIGHTBOX * 0.5 : ZOOM_LIGHTBOX;
        if (this.zoom >= lbThreshold) {
            this.openLightbox(item.id);
        } else {
            const screenCX = (item._x + ITEM_W / 2) * this.zoom + this.panX;
            const screenCY = (this._itemVisualY(item) + item._h / 2) * this.zoom + this.panY;
            this.animateZoom(this.zoom * 2.5, screenCX, screenCY);
        }
    }

    _centerItem() {
        const cx = (this.W / 2 - this.panX) / this.zoom;
        const cy = (this.H / 2 - this.panY) / this.zoom;
        let best = null, bestDist = Infinity;
        for (const item of this.items) {
            const vy = this._itemVisualY(item);
            const dx = (item._x + ITEM_W / 2) - cx;
            const dy = (vy + item._h / 2) - cy;
            const d  = dx * dx + dy * dy;
            if (d < bestDist) { bestDist = d; best = item; }
        }
        return best;
    }

    itemAt(sx, sy) {
        const wx = (sx - this.panX) / this.zoom;
        const wy = (sy - this.panY) / this.zoom;
        for (const item of this.items) {
            const vy = this._itemVisualY(item);
            if (wx >= item._x && wx <= item._x + ITEM_W &&
                wy >= vy && wy <= vy + item._h) return item;
        }
        return null;
    }

    // ── Lightbox ──────────────────────────────────────────────────────────────

    async openLightbox(id) {
        // Scope the lightbox to the currently visible looks so prev/next stays
        // within the active filter (otherwise PhotoSwipe pages through every look).
        const pool  = this.items.filter(i => this._itemVisible(i));
        const index = pool.findIndex(i => i.id === id);
        if (index < 0) return;

        const vpRect = this.viewport.getBoundingClientRect();

        const ARROW_PREV = '<div class="pswp-arrow"><svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-6"><path stroke-linecap="square" stroke-linejoin="square" d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18"/></svg></div>';
        const ARROW_NEXT = '<div class="pswp-arrow"><svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-6"><path stroke-linecap="square" stroke-linejoin="square" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" /></svg></div>';

        const pswp = new PhotoSwipe({
            trapFocus:    false,
            returnFocus:  false,
            arrowPrevSVG: ARROW_PREV,
            arrowNextSVG: ARROW_NEXT,
            closeSVG: '<svg xmlns="http://www.w3.org/2000/svg" width="19" height="19" viewBox="0 0 19 19" fill="none"><path d="M0.353546 0.353546L17.8535 17.8535" stroke="white"/><path d="M17.8535 0.353546L0.353546 17.8535" stroke="white"/></svg>',
            zoomSVG: '<span class="inline-block">−</span><span class="inline-block">+</span>',
            dataSource: pool.map(item => ({
                src:    this.fullUrl(item),
                msrc:   this.bestThumbUrl(item),
                width:  item.img_w || ITEM_W * 4,
                height: item.img_h || ITEM_H * 4,
            })),
            index,
            bgOpacity:          0.95,
            secondaryZoomLevel: 1,
            maxZoomLevel:       4,
            getThumbBoundsFn: (i) => {
                const it = pool[i];
                if (!it) return null;
                return {
                    x: it._x * this.zoom + this.panX + vpRect.left,
                    y: this._itemVisualY(it) * this.zoom + this.panY + vpRect.top,
                    w: ITEM_W * this.zoom,
                };
            },
        });

        const onSlide = () => {
            const c = pswp.currIndex;
            [c - 1, c, c + 1].forEach(i => {
                if (i >= 0 && i < pool.length) this.ensureDims(pool[i]);
            });
            const item = pool[c];
            if (item) {
                const slug = this.titleToSlug(item.title);
                history.replaceState(null, '', '#item=' + slug);
                this.updateCrumb(item);
                const shareUrl = `${location.origin}${location.pathname}?item=${slug}`;
                showShare(item, shareUrl);
            }
        };
        pswp.on('change', onSlide);
        pswp.on('openingAnimationEnd', onSlide);

        pswp.on('beforeOpen', () => document.body.classList.add('lightbox-open'));
        pswp.on('close', () => {
            document.body.classList.remove('lightbox-open');
            history.replaceState(null, '', window.location.pathname + window.location.search);
            this.updateCrumb(this._crumbItem);
            hideShare();
        });

        pswp.on('loadComplete', e => {
            const slide = e.slide;
            const img   = slide.content.element;
            if (img && img.naturalWidth) {
                slide.data.width  = img.naturalWidth;
                slide.data.height = img.naturalHeight;
                slide.updateContentSize(true);
            }
        });

        pswp.init();
    }

    ensureDims(item) {
        if (item._dims) return Promise.resolve();
        return new Promise(resolve => {
            const img = new Image();
            img.onload  = () => { item._dims = { w: img.naturalWidth, h: img.naturalHeight }; resolve(); };
            img.onerror = () => { item._dims = { w: 0, h: 0 }; resolve(); };
            img.src = this.fullUrl(item);
        });
    }

    // ── URL param ─────────────────────────────────────────────────────────────

    titleToSlug(title) {
        return title.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    }

    checkUrlParam() {
        const params      = new URLSearchParams(window.location.search);
        const itemParam   = params.get('item') || new URLSearchParams(window.location.hash.slice(1)).get('item');
        const filterParam = params.get('filter');

        if (filterParam) {
            // Resolve slug (e.g. "ss12") to collection ID, or use as-is if already an ID
            const filterKey = this.groups.find(g => g.label.toLowerCase() === filterParam.toLowerCase())?.key || filterParam;
            this.setFilter(filterKey, { navigateToFirst: false });
            this.frameCollection(filterKey);
        }

        if (itemParam) {
            const item = this.items.find(i => i.id === itemParam || this.titleToSlug(i.title) === itemParam);
            if (item) this.navigateTo(item.id, true, false);
        }
    }

    navigateTo(id, openLb = true, animate = true) {
        const item = this.items.find(i => i.id === id);
        if (!item) return;

        const targetZ  = Math.max(this.zoom, this.labelFullZ ?? ZOOM_LIGHTBOX * 0.5);
        // When centred, this item's _groupOffset will be ~0, so raw _y is accurate
        const endX     = this.W / 2 - (item._x + ITEM_W / 2) * targetZ;
        const endY     = this.H / 2 - (item._y + item._h / 2) * targetZ;

        this.zoomVel = 0;
        this.velX = this.velY = 0;

        if (!animate) {
            this.zoom = targetZ;
            this.panX = endX;
            this.panY = endY;
            this._syncWorld();
            this._wake();
            if (openLb) requestAnimationFrame(() => this.openLightbox(id));
            return;
        }

        const startZ  = this.zoom;
        const startX  = this.panX;
        const startY  = this.panY;
        const startTime = performance.now();
        const tick = now => {
            const t = Math.min(1, (now - startTime) / 500);
            const e = t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
            this.zoom = startZ + (targetZ - startZ) * e;
            this.panX = startX + (endX - startX) * e;
            this.panY = startY + (endY - startY) * e;
            this._syncWorld();
            this._wake();
            if (t < 1) {
                requestAnimationFrame(tick);
            } else {
                this._wake();
                if (openLb) setTimeout(() => this.openLightbox(id), 200);
            }
        };
        requestAnimationFrame(tick);
    }

    // ── Breadcrumb ────────────────────────────────────────────────────────────

    updateCrumb(item) {
        const el = document.getElementById('gallery-crumb');
        if (!el) return;

        // The separator owns its leading space as &nbsp;: #breadcrumbs-content is
        // display:flex, so this crumb is a blockified flex item and a plain
        // leading space would be stripped as start-of-line whitespace — which is
        // what produced "COLLECTIONS/ SS14". Parts are concatenated with no
        // whitespace between tags so that nbsp is the ONLY space; leaving the
        // markup indented would collapse to a second space on every separator
        // but the first, making them uneven.
        const sep  = '<span class="hidden md:inline">&nbsp;/ </span>';
        const part = text => `<span class="block md:inline">${text}</span>`;

        if (!item) {
            const label = this.groups.find(g => g.key === this._activeFilter)?.label;
            el.innerHTML = label ? sep + part(label) : '';
            return;
        }

        const m = (item.title || '').match(/^([A-Z]{2}\d{2})\s+(.+)$/);
        el.innerHTML = m
            ? sep + part(m[1]) + sep + part(m[2])
            : sep + part(item.title);
    }
}

import { GalleryAnimateZoomDOM } from './gallery-animate-zoom-dom.js';
import { GalleryWorldview }        from './gallery-worldview.js';
import { GalleryAccessoryClusters } from './gallery-accessory-clusters.js';

// Mobile world view (full archive on one pan/zoom surface, strip-backed).
// Default since 2026-07-13 after passing the real-device gate
// (docs/testing-mobile.md); ?worldview=0 is the kill switch — session-
// persistent, reverts to the single-collection view with no redeploy.
const DEFAULT_WORLDVIEW = true;

export function init() {
    // Always tear down the previous instance first — even when navigating to a
    // page that has no gallery — so its RAF loop and in-flight thumbnail loads
    // stop. swup keeps JS alive across navigations; this won't happen on its own.
    window.JK.gallery?.destroy();
    window.JK.gallery = null;
    const vp = document.getElementById('gallery-viewport');
    if (!vp) return;
    if (vp.dataset.variant === 'clusters') {
        window.JK.galleryVariant = 'clusters';
        window.JK.gallery = new GalleryAccessoryClusters();
        return;
    }
    // Route to the DOM variant on iOS (Pixi/WebGL is unreliable there) and on
    // all devices under tablet width — Android phones have similar memory and
    // image-decode pressure, just less dramatically. Desktop and tablets keep
    // the PixiJS canvas path which is well-tested at scale on those devices.
    // ?nodom=1 forces canvas anywhere as a diagnostic escape hatch.
    const params      = new URLSearchParams(location.search);
    const forceCanvas = params.has('nodom');
    const useDOM      = !forceCanvas && (IS_IOS || window.innerWidth < MOBILE_W);
    // ?worldview=1 opts this session into the world view (survives swup navs,
    // which rewrite the URL); ?worldview=0 opts out — kill switch after the
    // default flips.
    const gv = params.get('worldview');
    if (gv === '1') sessionStorage.setItem('jk_worldview', '1');
    if (gv === '0') sessionStorage.setItem('jk_worldview', '0');
    const stored  = sessionStorage.getItem('jk_worldview');
    const worldview = stored ? stored === '1' : DEFAULT_WORLDVIEW;
    // Stable variant id for diagnostics/tests (class names are minified in prod).
    window.JK.galleryVariant = useDOM ? (worldview ? 'worldview' : 'dom') : 'canvas';
    window.JK.gallery = useDOM
        ? (worldview ? new GalleryWorldview() : new GalleryAnimateZoomDOM())
        : new GalleryAnimateZoom();
}
